// FormikRichTreeView3.tsx
import * as React from 'react';
import { useField, useFormikContext } from 'formik';
import { Box, Typography, FormHelperText } from '@mui/material';
import { RichTreeView, TreeViewBaseItem, RichTreeViewProps } from '@mui/x-tree-view';

interface FormikRichTreeViewProps<T extends TreeViewBaseItem>
  extends Omit<
    RichTreeViewProps<T, true>,
    'selectedItems' | 'onSelectedItemsChange' | 'multiSelect' | 'checkboxSelection' | 'onItemClick'
  > {
  name: string;
  label?: string;
  initialSelectedIds?: string[];
  items: readonly T[];
}

/**
 * Рекурсивно собирает ID самого узла и ВСЕХ его потомков (детей, внуков и т.д.)
 */
const getSelfAndDescendantIds = (item: TreeViewBaseItem): string[] => {
  let ids: string[] = [item.id];
  if (item.children && item.children.length > 0) {
    item.children.forEach(child => {
      ids.push(...getSelfAndDescendantIds(child));
    });
  }
  return ids;
};

/**
 * Глубокий поиск узла по ID
 */
const findItemById = (items: readonly TreeViewBaseItem[], id: string): TreeViewBaseItem | null => {
  for (const item of items) {
    if (item.id === id) return item;
    if (item.children) {
      const found = findItemById(item.children, id);
      if (found) return found;
    }
  }
  return null;
};

export function FormikRichTreeView<T extends TreeViewBaseItem>({
  name,
  label,
  initialSelectedIds,
  items,
  ...props
}: FormikRichTreeViewProps<T>) {
  
  const [field, meta] = useField<string[]>(name);
  const { setFieldValue, setFieldTouched } = useFormikContext();

  const currentSelectedItems = React.useMemo(() => field.value || [], [field.value]);

  React.useEffect(() => {
    if (initialSelectedIds && (!field.value || field.value.length === 0)) {
      setFieldValue(name, initialSelectedIds);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialSelectedIds, name]);

  /**
   * Полностью ручное управление выбором через клик по элементу
   */
  const handleItemClick = React.useCallback(
    (event: React.SyntheticEvent, itemId: string) => {
      // Предотвращаем дефолтное поведение MUI, которое выделяет всё подряд
      event.preventDefault();

      const clickedItem = findItemById(items, itemId);
      if (!clickedItem) return;

      // Получаем массив ID: сам кликнутый элемент + все его дети на любую глубину
      const targetIds = getSelfAndDescendantIds(clickedItem);

      // Проверяем, выделен ли СЕЙЧАС сам элемент, по которому кликнули
      const isCurrentlySelected = currentSelectedItems.includes(itemId);

      let updatedSelectedItems: string[];

      if (isCurrentlySelected) {
        // ЕСЛИ БЫЛ ВЫДЕЛЕН -> Снимаем выделение с него и со ВСЕХ его детей
        updatedSelectedItems = currentSelectedItems.filter(id => !targetIds.includes(id));
      } else {
        // ЕСЛИ НЕ БЫЛ ВЫДЕЛЕН -> Выделяем его (и опционально всех его детей)
        // Если дети НЕ должны выделяться при апе, замените на: [...currentSelectedItems, itemId]
        updatedSelectedItems = Array.from(new Set([...currentSelectedItems, ...targetIds]));
      }

      setFieldValue(name, updatedSelectedItems);
      setFieldTouched(name, true, false);
    },
    [currentSelectedItems, items, name, setFieldTouched, setFieldValue],
  );

  const hasError = meta.touched && !!meta.error;

  // Собираем ID только реальных родителей для дефолтного раскрытия веток
  const defaultExpanded = React.useMemo(() => {
    const getParents = (nodes: readonly TreeViewBaseItem[]): string[] => {
      let ids: string[] = [];
      nodes.forEach(n => {
        if (n.children && n.children.length > 0) {
          ids.push(n.id);
          ids.push(...getParents(n.children));
        }
      });
      return ids;
    };
    return getParents(items);
  }, [items]);

  return (
    <Box
      sx={{
        height: 260,
        flexGrow: 1,
        maxWidth: 400,
        overflowY: 'auto',
        border: hasError ? '1px solid #d32f2f' : '1px solid #ccc',
        borderRadius: 1,
        p: 1,
      }}
    >
      {label && (
        <Typography 
          variant="caption" 
          sx={{ display: 'block', mb: 1, color: hasError ? '#d32f2f' : 'text.secondary', fontWeight: 500 }}
        >
          {label}
        </Typography>
      )}
      
      <RichTreeView
        {...props}
        items={items}
        multiSelect
        checkboxSelection
        selectedItems={currentSelectedItems}
        // Используем onItemClick вместо onSelectedItemsChange для точечного контроля
        onItemClick={handleItemClick}
        aria-invalid={hasError}
        defaultExpandedItems={defaultExpanded}
      />
      
      {hasError && <FormHelperText error>{meta.error}</FormHelperText>}
    </Box>
  );
}